import React, { useCallback, useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import {useTranslations} from "use-intl";

import { ModalProps } from '@tager/web-components';
import { Nullable } from '@tager/web-core';

import { useSettingItem } from '@/hooks';
import ContentContainer from '@/components/ContentContainer';
import {
  ActionType,
  CalcAction,
  initStateKeys,
} from '@/modules/Rassrochka/components/Calculator/Clculator.typings';
import {
  getFinalPrice,
  getQuarterToString,
} from '@/modules/Rassrochka/components/Calculator/Calculator.helpers';
import { colors } from '@/constants/theme';
import { ReactComponent as CloseIcon } from '@/assets/svg/Modals/close.svg';
import { normalizePrice } from '@/utils/common';
import UseModalsOpen from '@/hooks/useModalsOpen';
import { useBodyPositionFixed } from '@/hooks/useBodyPositionFixed';

import CalculatorInput from './components/CalculatorInput';
import CalculatorSelect from './components/CalculatorSelect';
import Output from './components/Output';

const initOutputState = {
  price: 0,
  underpayment: 0,
  monthlyPayment: 0,
};

const initialState: Record<initStateKeys, number> = {
  periodSelected: 0,
  price: 1000,
  initPaymentRate: 25,
  rate: 0,
  initialPayment: getQuarterToString(1000, 25),
};

const calcReducer = (state: typeof initialState, action: CalcAction) => {
  const { type, payload } = action;
  switch (type) {
    case ActionType.SETPERIOD:
      return {
        ...state,
        periodSelected: payload,
      };
    case ActionType.SETPRICE:
      return {
        ...state,
        price: payload,
      };
    case ActionType.SETINITPAYMENTRATE:
      return {
        ...state,
        initPaymentRate: payload,
      };
    case ActionType.SETRATE:
      return {
        ...state,
        rate: payload,
      };
    case ActionType.SETINITIALPAYMENT:
      return {
        ...state,
        initialPayment: payload,
      };
    default:
      return state;
  }
};

type CardCalculatorProps = ModalProps<{
  productPrice: Nullable<number> | undefined;
}>;

function ProductCalculator({ closeModal, innerProps }: CardCalculatorProps) {
  const [state, dispatch] = useReducer(calcReducer, initialState);
  const [outputData, setOutputData] = useState(initOutputState);

  const creditPrices = useSettingItem('PRICES_CREDIT');
  const { closeModalState, openModalState } = UseModalsOpen();

  const setPeriodAndRate = useCallback(
    (periodIndex: number) => {
      const period = creditPrices[periodIndex].period;
      const rate = creditPrices[periodIndex].underPayment;
      if (period && Number(period) !== state.periodSelected) {
        dispatch({ type: ActionType.SETPERIOD, payload: Number(period) });
        dispatch({ type: ActionType.SETRATE, payload: Number(rate) });
      }
    },
    [creditPrices, state.periodSelected]
  );

  const getFirstCost = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: ActionType.SETINITIALPAYMENT,
      payload: Number(e.target.value.trim()),
    });
  };

  useEffect(() => {
    const { period, initial, underPayment } = creditPrices[0];
    if (period) {
      dispatch({ type: ActionType.SETPERIOD, payload: Number(period) });
    }
    if (initial) {
      dispatch({
        type: ActionType.SETINITPAYMENTRATE,
        payload: Number(initial),
      });
    }
    if (underPayment) {
      dispatch({ type: ActionType.SETRATE, payload: Number(underPayment) });
    }
  }, [creditPrices]);

  useBodyPositionFixed();

  useEffect(() => {
    const data = getFinalPrice(
      innerProps.productPrice ?? 1000,
      state.rate,
      state.initialPayment,
      state.periodSelected
    );
    setOutputData(data);
  }, [
    state.rate,
    state.initialPayment,
    state.periodSelected,
    innerProps.productPrice,
  ]);

  const currency = useSettingItem('CURRENCY');
  const t = useTranslations();

  return (
    <Component>
      <Header>
        <Title>{t('rassrochka.title')}</Title>
        <StyledIcon
          onClick={() => {
            closeModal();
          }}
        />
      </Header>
      <ContentContainer>
        <Content>
          <Item>
            <Label>{t('rassrochka.price')}, {currency}</Label>
            {innerProps?.productPrice && innerProps.productPrice !== 0 ? (
              <Value>{normalizePrice(innerProps.productPrice)} {currency}</Value>
            ) : null}
          </Item>
          <Item>
            <Label>{t('rassrochka.period')}</Label>
            <CalculatorSelect setData={setPeriodAndRate} data={creditPrices} />
          </Item>
          <Item>
            <Label>{t('rassrochka.initial_payment')} ({t('dimensions.from')} 25%)</Label>
            <CalculatorInput
              value={state.initialPayment}
              icon="wallet"
              onChange={getFirstCost}
            />
          </Item>
          <Output output={outputData} />
        </Content>
      </ContentContainer>
    </Component>
  );
}

export default ProductCalculator;

const Component = styled.div<{ addPadding?: boolean }>`
  background: ${colors.white};
  padding-bottom: 17px;
  width: min(100vw, 500px);
  height: min(calc(var(--vh, 1) * 100), 80vh);
  -webkit-overflow-scrolling: touch !important;
  overflow: auto !important;
`;

const Content = styled.div``;

const Header = styled.div`
  height: 45px;
  position: relative;
  padding: 5px 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 6px 0 #d0d0d0;
`;

const StyledIcon = styled(CloseIcon)`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  cursor: pointer;
  margin-left: 15px;
`;
const Title = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
`;

const Item = styled.div`
  margin-top: 19px;
  max-width: 480px;
`;
const Label = styled.div`
  margin-bottom: 7px;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;
`;
const Value = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 26px;
`;
